package com.example.socialmediaapp.ui.screens

import androidx.compose.foundation.background
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.dp
import com.example.socialmediaapp.currentTimeMillis
import com.example.socialmediaapp.data.models.Message
import com.example.socialmediaapp.network.ChatRepository
import com.example.socialmediaapp.utils.TimeFormatter
import kotlinx.coroutines.launch

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ChatDetailScreen(
    chatId: String,
    chatName: String = "Chat",
    currentUserId: String,
    chatRepository: ChatRepository? = null,
    onBack: () -> Unit,
    onVideoCall: () -> Unit = {},
    onVoiceCall: () -> Unit = {},
    // Legacy params for compat
    messages: kotlinx.coroutines.flow.Flow<List<Message>>? = null,
    onSendMessage: (String) -> Unit = {}
) {
    val scope = rememberCoroutineScope()
    var messageInput by remember { mutableStateOf("") }
    var isSelfDestruct by remember { mutableStateOf(false) }
    val listState = rememberLazyListState()

    // Use repository flow if available, otherwise fall back to legacy flow
    val activeFlow = remember(chatRepository, chatId, messages) {
        chatRepository?.messagesFlow(chatId)
            ?: messages
            ?: kotlinx.coroutines.flow.MutableStateFlow(emptyList())
    }
    val messageList by activeFlow.collectAsState(initial = emptyList())

    // Load message history on enter
    LaunchedEffect(chatId, chatRepository) {
        chatRepository?.getMessages(chatId)
    }

    // Scroll to bottom on new messages
    LaunchedEffect(messageList.size) {
        if (messageList.isNotEmpty()) listState.animateScrollToItem(0)
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(chatName) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, "Back")
                    }
                },
                actions = {
                    IconButton(onClick = onVideoCall) {
                        Icon(Icons.Default.Videocam, "Video Call")
                    }
                    IconButton(onClick = onVoiceCall) {
                        Icon(Icons.Default.Call, "Call")
                    }
                }
            )
        }
    ) { padding ->
        Column(
            modifier = Modifier
                .fillMaxSize()
                .padding(padding)
        ) {
            if (messageList.isEmpty()) {
                Box(
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                    contentAlignment = Alignment.Center
                ) {
                    Column(
                        horizontalAlignment = Alignment.CenterHorizontally,
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        Icon(
                            Icons.Default.ChatBubbleOutline,
                            contentDescription = null,
                            modifier = Modifier.size(48.dp),
                            tint = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                        Text("No messages yet", style = MaterialTheme.typography.titleSmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant)
                        Text("Say hello!", style = MaterialTheme.typography.bodySmall,
                            color = MaterialTheme.colorScheme.onSurfaceVariant)
                    }
                }
            } else {
                LazyColumn(
                    state = listState,
                    modifier = Modifier.weight(1f),
                    reverseLayout = true
                ) {
                    items(messageList.reversed(), key = { it.id }) { message ->
                        MessageBubble(message = message, currentUserId = currentUserId)
                    }
                }
            }

            // Input bar
            Column(modifier = Modifier.fillMaxWidth().background(MaterialTheme.colorScheme.surface)) {
                if (isSelfDestruct) {
                    Row(
                        modifier = Modifier.fillMaxWidth().padding(horizontal = 16.dp, vertical = 4.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Icon(
                            Icons.Default.Timer,
                            contentDescription = null,
                            tint = MaterialTheme.colorScheme.error,
                            modifier = Modifier.size(16.dp)
                        )
                        Spacer(modifier = Modifier.width(8.dp))
                        Text(
                            "Self-destruct active (10s)",
                            style = MaterialTheme.typography.labelMedium,
                            color = MaterialTheme.colorScheme.error
                        )
                    }
                }
                
                Row(
                    modifier = Modifier.fillMaxWidth().padding(8.dp),
                    verticalAlignment = Alignment.Bottom
                ) {
                    IconButton(onClick = { isSelfDestruct = !isSelfDestruct }) {
                        Icon(
                            Icons.Default.Timer,
                            "Self Destruct",
                            tint = if (isSelfDestruct) MaterialTheme.colorScheme.error else MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }

                    TextField(
                        value = messageInput,
                        onValueChange = { messageInput = it },
                        modifier = Modifier.weight(1f),
                        placeholder = { Text("Message") },
                        maxLines = 4,
                        colors = TextFieldDefaults.colors(
                            focusedIndicatorColor = Color.Transparent,
                            unfocusedIndicatorColor = Color.Transparent
                        ),
                        shape = MaterialTheme.shapes.large
                    )

                    IconButton(
                        onClick = {
                            val text = messageInput.trim()
                            if (text.isNotBlank()) {
                                messageInput = ""
                                scope.launch {
                                    if (chatRepository != null) {
                                        chatRepository.sendMessage(
                                            chatId = chatId,
                                            content = text,
                                            expiresInSeconds = if (isSelfDestruct) 10L else null
                                        )
                                        // Optimistic local update
                                        chatRepository.onIncomingMessage(
                                            Message(
                                                id = "local_${currentTimeMillis()}",
                                                chatId = chatId,
                                                senderId = currentUserId,
                                                content = text,
                                                timestamp = currentTimeMillis()
                                            )
                                        )
                                    } else {
                                        onSendMessage(text)
                                    }
                                }
                            }
                        },
                        enabled = messageInput.isNotBlank()
                    ) {
                        Icon(
                            Icons.Default.Send,
                            "Send",
                            tint = if (messageInput.isNotBlank())
                                MaterialTheme.colorScheme.primary
                            else
                                MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                }
            }
        }
    }
}

@Composable
fun MessageBubble(message: Message, currentUserId: String) {
    val isSent = message.senderId == currentUserId

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = 8.dp, vertical = 4.dp),
        horizontalArrangement = if (isSent) Arrangement.End else Arrangement.Start
    ) {
        if (!isSent) {
            Box(
                modifier = Modifier
                    .size(32.dp)
                    .clip(CircleShape)
                    .background(MaterialTheme.colorScheme.secondaryContainer),
                contentAlignment = Alignment.Center
            ) {
                Text(
                    text = message.senderName?.firstOrNull()?.uppercase() ?: "?",
                    style = MaterialTheme.typography.labelMedium
                )
            }
            Spacer(modifier = Modifier.width(8.dp))
        }

        Column(
            modifier = Modifier.widthIn(max = 280.dp),
            horizontalAlignment = if (isSent) Alignment.End else Alignment.Start
        ) {
            Surface(
                color = if (isSent)
                    MaterialTheme.colorScheme.primaryContainer
                else
                    MaterialTheme.colorScheme.secondaryContainer,
                shape = MaterialTheme.shapes.large
            ) {
                Column(modifier = Modifier.padding(12.dp)) {
                    if (message.deletedAt != null) {
                        Text(
                            "Message deleted",
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            fontStyle = androidx.compose.ui.text.font.FontStyle.Italic
                        )
                    } else {
                        Text(message.content, style = MaterialTheme.typography.bodyMedium)
                        if (message.editedAt != null) {
                            Text("edited", style = MaterialTheme.typography.labelSmall,
                                color = MaterialTheme.colorScheme.onSurfaceVariant)
                        }
                    }
                }
            }

            Row(
                modifier = Modifier.padding(top = 2.dp, start = 4.dp, end = 4.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    text = TimeFormatter.formatTime(message.timestamp),
                    style = MaterialTheme.typography.labelSmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                if (isSent) {
                    Spacer(modifier = Modifier.width(4.dp))
                    Icon(
                        imageVector = if (message.readAt != null) Icons.Default.DoneAll else Icons.Default.Done,
                        contentDescription = if (message.readAt != null) "Read" else "Sent",
                        modifier = Modifier.size(14.dp),
                        tint = if (message.readAt != null) Color(0xFF4A9EFF)
                               else MaterialTheme.colorScheme.onSurfaceVariant
                    )
                }
            }
        }

        if (isSent) Spacer(modifier = Modifier.width(8.dp))
    }
}
